import React, { useState, useEffect } from "react";
import { adminApi } from "../../api";
import {
  BarChart3,
  Package,
  Layers,
  MousePointer,
  TrendingUp,
  ArrowUpRight,
  Clock,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

const StatCard = ({ title, value, icon: Icon, trend, color = "#007AFF" }) => (
  <div className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-white/20 transition-all">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[#A1A1AA] text-sm font-mono">{title}</p>
        <p className="text-3xl font-bold mt-2 text-white">{value}</p>
        {trend && (
          <div className="flex items-center gap-1 mt-2 text-emerald-400 text-sm">
            <TrendingUp size={14} />
            <span>{trend}</span>
          </div>
        )}
      </div>
      <div
        className="p-3 rounded-lg"
        style={{ backgroundColor: `${color}20` }}
      >
        <Icon size={24} style={{ color }} />
      </div>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [niches, setNiches] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, nichesRes, productsRes] = await Promise.all([
          adminApi.getAnalytics(),
          adminApi.getNiches(),
          adminApi.getProducts(),
        ]);
        setAnalytics(analyticsRes.data);
        setNiches(nichesRes.data);
        setProducts(productsRes.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Prepare chart data
  const sourceData = analytics?.clicks_by_source
    ? Object.entries(analytics.clicks_by_source).map(([name, value]) => ({
        name,
        clicks: value,
      }))
    : [];

  const topProductsData = analytics?.top_products?.slice(0, 5) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-pulse text-[#A1A1AA]">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-sans tracking-wide">
            Command Center
          </h1>
          <p className="text-[#A1A1AA] text-sm mt-1 font-mono">
            Monitor your affiliate empire
          </p>
        </div>
        <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
          <Clock size={14} />
          <span className="font-mono">
            {new Date().toLocaleString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="TOTAL CLICKS"
          value={analytics?.total_clicks || 0}
          icon={MousePointer}
          color="#007AFF"
        />
        <StatCard
          title="CLICKS TODAY"
          value={analytics?.clicks_today || 0}
          icon={TrendingUp}
          trend={analytics?.clicks_today > 0 ? "+12% vs yesterday" : null}
          color="#10B981"
        />
        <StatCard
          title="ACTIVE NICHES"
          value={niches.length}
          icon={Layers}
          color="#F59E0B"
        />
        <StatCard
          title="TOTAL PRODUCTS"
          value={products.length}
          icon={Package}
          color="#8B5CF6"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Clicks by Source */}
        <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
          <h3 className="text-white font-semibold mb-4">Clicks by Source</h3>
          {sourceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={sourceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                <XAxis dataKey="name" stroke="#A1A1AA" fontSize={12} />
                <YAxis stroke="#A1A1AA" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1A1A1A",
                    border: "1px solid #2A2A2A",
                    borderRadius: "8px",
                  }}
                />
                <Bar dataKey="clicks" fill="#007AFF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-[#A1A1AA]">
              No click data yet
            </div>
          )}
        </div>

        {/* Top Products */}
        <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
          <h3 className="text-white font-semibold mb-4">Top Products by Clicks</h3>
          {topProductsData.length > 0 ? (
            <div className="space-y-3">
              {topProductsData.map((product, index) => (
                <div
                  key={product.product_id}
                  className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-md"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-[#007AFF] font-mono text-sm w-6">
                      #{index + 1}
                    </span>
                    <span className="text-white">{product.product_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#A1A1AA] font-mono">
                      {product.clicks} clicks
                    </span>
                    <ArrowUpRight size={14} className="text-emerald-400" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-[#A1A1AA]">
              No product clicks yet
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
        <h3 className="text-white font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <a
            href="/admin/niches"
            className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5 hover:border-[#007AFF]/50 transition-all text-center group"
            data-testid="quick-action-add-niche"
          >
            <Layers className="mx-auto text-[#007AFF] mb-2 group-hover:scale-110 transition-transform" />
            <span className="text-sm text-[#A1A1AA]">Add Niche</span>
          </a>
          <a
            href="/admin/products"
            className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5 hover:border-[#007AFF]/50 transition-all text-center group"
            data-testid="quick-action-add-product"
          >
            <Package className="mx-auto text-[#10B981] mb-2 group-hover:scale-110 transition-transform" />
            <span className="text-sm text-[#A1A1AA]">Add Product</span>
          </a>
          <a
            href="/admin/content"
            className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5 hover:border-[#007AFF]/50 transition-all text-center group"
            data-testid="quick-action-generate-content"
          >
            <BarChart3 className="mx-auto text-[#F59E0B] mb-2 group-hover:scale-110 transition-transform" />
            <span className="text-sm text-[#A1A1AA]">Generate Content</span>
          </a>
          <a
            href="/"
            className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5 hover:border-[#007AFF]/50 transition-all text-center group"
            data-testid="quick-action-view-site"
          >
            <ArrowUpRight className="mx-auto text-[#8B5CF6] mb-2 group-hover:scale-110 transition-transform" />
            <span className="text-sm text-[#A1A1AA]">View Site</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
